import { parseApiDateAsUtc } from "./dateUtil";

// The backend MyOrders / Resource Manager / User Management APIs expose no sort
// parameter, so cross-page sorting cannot be done server-side. Instead the grids
// fetch the full result set for the active filters and let ag-grid paginate +
// sort client-side across ALL records.
//
// The backend caps PageSize at 100 (larger values return HTTP 400 "PageSize must
// be between 1 and 100"), so `fetchAllPages` retrieves every page at
// MAX_API_PAGE_SIZE and merges them into one response.
export const MAX_API_PAGE_SIZE = 100;

// Fetch every page for the active filters (each request capped at the backend's
// 100-row limit) and return a single result shaped like one page, but whose row
// array contains ALL records. `getRows`/`getTotal` read the row array and total
// count from a page result; `withRows` returns a copy of the first page with the
// merged rows. The returned shape matches a single page so reducers are unchanged.
export async function fetchAllPages({ fetchPage, getRows, getTotal, withRows }) {
  const pageSize = MAX_API_PAGE_SIZE;
  const first = await fetchPage({ pageNumber: 1, pageSize });
  const firstRows = getRows(first) || [];
  const total = Number(getTotal(first)) || firstRows.length;
  // Safety cap (100 pages = 10k rows) guards against a bad totalRecords value.
  const pageCount = Math.min(Math.ceil(total / pageSize) || 1, 100);
  if (pageCount <= 1) return first;

  let allRows = firstRows.slice();
  for (let page = 2; page <= pageCount; page++) {
    const res = await fetchPage({ pageNumber: page, pageSize });
    const rows = getRows(res);
    if (!Array.isArray(rows) || rows.length === 0) break;
    allRows = allRows.concat(rows);
  }
  return withRows(first, allRows);
}

const MONTH_ABBR = [
  "JAN", "FEB", "MAR", "APR", "MAY", "JUN",
  "JUL", "AUG", "SEP", "OCT", "NOV", "DEC",
];

export function formatDate(isoString) {
  if (!isoString) return "--";
  const d = parseApiDateAsUtc(isoString);
  if (!d) return isoString;
  const day = String(d.getDate()).padStart(2, "0");
  return `${day}-${MONTH_ABBR[d.getMonth()]}-${d.getFullYear()}`;
}

export function handleFilterChange(setter) {
  return (value) => {
    setter(Array.isArray(value) ? value : []);
  };
}

export function handleSearchInputChange(setter) {
  return (event) => {
    const value = event?.target ? event.target.value : event;
    setter(value || "");
  };
}
